import React, { createContext, useContext, useReducer, useEffect, useCallback } from "react";
import finacraft_api from "../../api_adapters/finacraft_api";

const STORAGE_PREFIX = "onboarding_client_";

const initialState = {
  clientId: null,
  bankAccounts: [],
  incomes: [],
  currency: "INR",
  currentStep: 0,
  // The last non-welcome step the user visited (used for Resume).
  resumeStep: 1,
  savedStep: 1,
  loaded: false,
  synced: false,
};

// Helper functions
// Temp IDs are timestamps (Date.now()), which are currently > 1e12.
const isDraft = (entity) => {
  return !entity || !entity.id || (typeof entity.id === "number" && entity.id > 1000000000000);
};

// Treat anything with a numeric, non-temp ID as "saved", regardless of created_at.
// This keeps behavior correct even when update endpoints don't return created_at.
const isSaved = (entity) => {
  return !!(entity && typeof entity.id === "number" && entity.id < 1000000000000);
};

const normalizeCheckpointData = (rawData = {}) => {
  let bankAccounts = [];
  let currency = undefined;

  if (Array.isArray(rawData.bankAccounts)) {
    bankAccounts = rawData.bankAccounts;
  } else if (
    rawData.bankAccounts &&
    (Array.isArray(rawData.bankAccounts.accounts) || rawData.bankAccounts.accounts)
  ) {
    bankAccounts = Array.isArray(rawData.bankAccounts.accounts)
      ? rawData.bankAccounts.accounts
      : [];
    currency = rawData.bankAccounts.currency;
  }

  const incomes = Array.isArray(rawData.incomes) ? rawData.incomes : [];

  // Deduplicate and clean bank accounts:
  // - Keep all saved accounts (those with IDs)
  // - Drop any draft account that has the same name as a saved account
  const savedAccounts = bankAccounts.filter((acc) => acc && typeof acc.id === "number");
  const savedNames = new Set(
    savedAccounts.map((acc) => (acc && typeof acc.name === "string" ? acc.name : null)).filter(
      Boolean
    )
  );
  const draftAccounts = bankAccounts.filter((acc) => !acc || !acc.id);
  const cleanedDrafts = draftAccounts.filter(
    (acc) => !acc || !acc.name || !savedNames.has(acc.name)
  );
  const cleanedBankAccounts = [...savedAccounts, ...cleanedDrafts];

  const normalized = {
    bankAccounts: cleanedBankAccounts,
    incomes,
  };
  if (currency) normalized.currency = currency;
  if (rawData.currency) normalized.currency = rawData.currency;

  return normalized;
};

function reducer(state, action) {
  switch (action.type) {
    case "init":
      return {
        ...state,
        clientId: action.clientId,
        ...action.payload,
        loaded: true,
      };
    case "setBankAccounts":
      return { ...state, bankAccounts: action.bankAccounts };
    case "setIncomes":
      return { ...state, incomes: action.incomes };
    case "setCurrency":
      return { ...state, currency: action.currency };
    case "setCurrentStep":
      return { ...state, currentStep: action.step };
    case "setSavedStep":
      return { ...state, savedStep: action.step };
    case "setStep":
      return {
        ...state,
        currentStep: action.step,
        // Track furthest completed step for "progress".
        savedStep: action.step > state.savedStep ? action.step : state.savedStep,
        // Track last active non-welcome step for Resume behavior.
        resumeStep:
          action.step > 0
            ? action.step
            : state.resumeStep,
      };
    case "setSynced":
      return { ...state, synced: action.synced };
    case "apply":
      return { ...state, ...action.payload };
    default:
      return state;
  }
}

const OnboardingContext = createContext(null);

export function OnboardingProvider({ clientId, children }) {
  const [state, dispatch] = useReducer(reducer, { ...initialState, clientId });

  // Load checkpoint from localStorage when clientId changes
  useEffect(() => {
    if (!clientId) return;
    const key = `${STORAGE_PREFIX}${clientId}`;
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : {};
      const payloadData = parsed.data || {};

      const normalized = normalizeCheckpointData(payloadData);
      const step = parsed.step || 1;
      const persistedResumeStep =
        typeof parsed.currentStep === "number" && parsed.currentStep >= 0
          ? parsed.currentStep
          : 0;

      dispatch({
        type: "init",
        clientId,
        payload: {
          ...normalized,
          savedStep: step,
          // Always start on Welcome after refresh.
          currentStep: 0,
          // Resume target (last active non-welcome step).
          resumeStep: persistedResumeStep > 0 ? persistedResumeStep : step || 1,
        },
      });
    } catch (e) {
      console.error("Error loading onboarding checkpoint", e);
      dispatch({ type: "init", clientId, payload: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Persist to localStorage whenever relevant parts change
  useEffect(() => {
    if (!clientId || !state.loaded) return;
    const key = `${STORAGE_PREFIX}${clientId}`;
    const payload = {
      data: {
        bankAccounts: state.bankAccounts,
        incomes: state.incomes,
        currency: state.currency,
      },
      // Backward-compat: `step` keeps meaning "furthest completed step".
      step: state.savedStep,
      // Persist resume target under `currentStep` for backward compatibility.
      currentStep: state.resumeStep,
    };
    try {
      localStorage.setItem(key, JSON.stringify(payload));
      // Also dispatch a same-window custom event so code relying on that continues to work
      try {
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("onboarding:checkpoint-updated", { detail: { clientId } })
          );
        }
      } catch (e) {
        // ignore
      }
    } catch (e) {
      console.error("Error saving onboarding checkpoint", e);
    }
  }, [
    clientId,
    state.bankAccounts,
    state.incomes,
    state.currency,
    state.savedStep,
    state.resumeStep,
    state.loaded,
  ]);

  // Multi-tab sync: listen to storage events
  useEffect(() => {
    const onStorage = (ev) => {
      if (!ev.key) return;
      if (ev.key !== `${STORAGE_PREFIX}${clientId}`) return;
      try {
        const parsed = ev.newValue ? JSON.parse(ev.newValue) : {};
        const normalized = normalizeCheckpointData(parsed.data || {});
        const step = parsed.step;
        const resumeStep =
          typeof parsed.currentStep === "number" && parsed.currentStep >= 0
            ? parsed.currentStep
            : undefined;

        const payload = { ...normalized };
        if (typeof step === "number") {
          payload.savedStep = step;
        }
        if (typeof resumeStep === "number") {
          payload.resumeStep = resumeStep;
        }

        dispatch({ type: "apply", payload });
      } catch (e) {
        console.error("Error parsing storage event", e);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [clientId]);

  // Sync from backend (automatic on mount)
  const syncFromBackend = useCallback(async () => {
    if (!clientId || !state.loaded) return;
    try {
      const [bankAccountsResponse, incomesResponse] = await Promise.all([
        finacraft_api.get(`/core_api/bank-accounts/all-client-accounts/`),
        finacraft_api.get(`/core_api/incomes/all-client-incomes/`),
      ]);

      const savedBankAccounts = bankAccountsResponse.data || [];
      const savedIncomes = incomesResponse.data || [];

      // Merge saved entities with existing drafts
      const bankAccountsMap = new Map();
      const incomesMap = new Map();

      // Add saved entities
      savedBankAccounts.forEach((acc) => {
        if (acc.id) bankAccountsMap.set(acc.id, acc);
      });
      savedIncomes.forEach((inc) => {
        if (inc.id) incomesMap.set(inc.id, inc);
      });

      // Add draft entities (those without IDs or with temp IDs)
      state.bankAccounts.forEach((acc) => {
        if (isDraft(acc)) {
          // Draft accounts don't have IDs, so we can't use a Map key
          // Instead, we'll add them to the array
        } else if (acc.id && !bankAccountsMap.has(acc.id)) {
          bankAccountsMap.set(acc.id, acc);
        }
      });

      state.incomes.forEach((inc) => {
        if (isDraft(inc)) {
          // Draft incomes don't have IDs
        } else if (inc.id && !incomesMap.has(inc.id)) {
          incomesMap.set(inc.id, inc);
        }
      });

      // Combine saved and drafts
      const allBankAccounts = [
        ...Array.from(bankAccountsMap.values()),
        ...state.bankAccounts.filter(isDraft),
      ];
      const allIncomes = [
        ...Array.from(incomesMap.values()),
        ...state.incomes.filter(isDraft),
      ];

      dispatch({ type: "setBankAccounts", bankAccounts: allBankAccounts });
      dispatch({ type: "setIncomes", incomes: allIncomes });
      if (savedBankAccounts[0]?.currency) {
        dispatch({ type: "setCurrency", currency: savedBankAccounts[0].currency });
      }
      dispatch({ type: "setSynced", synced: true });
    } catch (error) {
      console.error("Error syncing from backend:", error);
      // Non-fatal: continue with local state
    }
  }, [clientId, state.loaded, state.bankAccounts, state.incomes]);

  // Automatic sync on mount (Option A)
  useEffect(() => {
    if (state.loaded && !state.synced) {
      syncFromBackend();
    }
  }, [state.loaded, state.synced, syncFromBackend]);

  // Bank Account CRUD methods
  const createBankAccount = useCallback(
    (draftData) => {
      const newAccount = {
        ...draftData,
        id: Date.now(), // Temporary ID for frontend tracking
        balance: parseFloat(draftData.balance),
      };
      dispatch({
        type: "setBankAccounts",
        bankAccounts: [...state.bankAccounts, newAccount],
      });
    },
    [state.bankAccounts]
  );

  const updateBankAccount = useCallback(
    async (id, data) => {
      const account = state.bankAccounts.find((acc) => acc.id === id);
      if (!account) {
        console.error("Account not found:", id);
        return;
      }

      const updatedAccount = {
        ...account,
        ...data,
        balance: parseFloat(data.balance),
      };

      // If saved, update via API
      if (isSaved(account)) {
        try {
          const response = await finacraft_api.patch(`/core_api/bank-accounts/${id}/`, {
            name: updatedAccount.name,
            balance: updatedAccount.balance,
            currency: updatedAccount.currency || state.currency,
          });
          // Merge the response over the existing account so we preserve
          // read-only fields like created_at even if the update serializer
          // doesn't include them.
          const savedAccount = {
            ...account,
            ...response.data,
          };
          dispatch({
            type: "setBankAccounts",
            bankAccounts: state.bankAccounts.map((acc) => (acc.id === id ? savedAccount : acc)),
          });
        } catch (error) {
          console.error("Error updating bank account:", error);
          alert("Error updating bank account. Please try again.");
          throw error;
        }
      } else {
        // If draft, update in state only
        dispatch({
          type: "setBankAccounts",
          bankAccounts: state.bankAccounts.map((acc) => (acc.id === id ? updatedAccount : acc)),
        });
      }
    },
    [state.bankAccounts, state.currency]
  );

  const deleteBankAccount = useCallback(
    async (id) => {
      const account = state.bankAccounts.find((acc) => acc.id === id);
      if (!account) {
        console.error("Account not found:", id);
        return null;
      }

      // Check for related incomes (saved + draft)
      const relatedIncomes = state.incomes.filter(
        (inc) => inc.bank_account === id || inc.account_id === id
      );

      // If saved account, check via API
      if (isSaved(account)) {
        if (relatedIncomes.length > 0) {
          // Return config for confirm modal
          return {
            show: true,
            modalSize: "modal-lg",
            title: "Confirm Delete",
            relatedIncomes,
            currency: state.currency || "INR",
            onConfirm: async () => {
              try {
                await finacraft_api.delete(`/core_api/bank-accounts/${id}/cascade-delete/`);
                // Remove account and related incomes from state
                dispatch({
                  type: "setBankAccounts",
                  bankAccounts: state.bankAccounts.filter((acc) => acc.id !== id),
                });
                dispatch({
                  type: "setIncomes",
                  incomes: state.incomes.filter(
                    (inc) => inc.bank_account !== id && inc.account_id !== id
                  ),
                });
                return true;
              } catch (error) {
                console.error("Error performing cascade delete:", error);
                alert("Error deleting account and associated incomes. Please try again.");
                return false;
              }
            },
            onCancel: () => {
              // Just close modal
            },
            id: `confirm-delete-${id}`,
          };
        } else {
          // No incomes, delete directly
          try {
            await finacraft_api.delete(`/core_api/bank-accounts/${id}/`);
            dispatch({
              type: "setBankAccounts",
              bankAccounts: state.bankAccounts.filter((acc) => acc.id !== id),
            });
            return true;
          } catch (error) {
            if (error?.response?.status === 412) {
              // Incomes exist (race condition), show confirm
              const incomes = error.response.data?.incomes || [];
              return {
                show: true,
                modalSize: "modal-lg",
                title: "Confirm Delete",
                relatedIncomes: [...relatedIncomes, ...incomes],
                currency: state.currency || "INR",
                onConfirm: async () => {
                  try {
                    await finacraft_api.delete(`/core_api/bank-accounts/${id}/cascade-delete/`);
                    dispatch({
                      type: "setBankAccounts",
                      bankAccounts: state.bankAccounts.filter((acc) => acc.id !== id),
                    });
                    dispatch({
                      type: "setIncomes",
                      incomes: state.incomes.filter(
                        (inc) => inc.bank_account !== id && inc.account_id !== id
                      ),
                    });
                    return true;
                  } catch (e) {
                    console.error("Error performing cascade delete:", e);
                    alert("Error deleting account and associated incomes. Please try again.");
                    return false;
                  }
                },
                onCancel: () => {},
                id: `confirm-delete-${id}`,
              };
            }
            console.error("Error deleting bank account:", error);
            alert("Error deleting bank account. Please try again.");
            return false;
          }
        }
      } else {
        // Draft account, delete from state only
        dispatch({
          type: "setBankAccounts",
          bankAccounts: state.bankAccounts.filter((acc) => acc.id !== id),
        });
        // Also remove related draft incomes
        if (relatedIncomes.length > 0) {
          dispatch({
            type: "setIncomes",
            incomes: state.incomes.filter(
              (inc) => inc.bank_account !== id && inc.account_id !== id
            ),
          });
        }
        return true;
      }
    },
    [state.bankAccounts, state.incomes, state.currency]
  );

  const bulkSaveBankAccounts = useCallback(async () => {
    const drafts = state.bankAccounts.filter(isDraft);
    if (drafts.length === 0) {
      return state.bankAccounts;
    }

    try {
      const payload = {
        accounts: drafts.map((acc) => ({
          name: acc.name,
          balance: acc.balance,
          currency: acc.currency || state.currency,
        })),
      };

      const response = await finacraft_api.post(`/core_api/bank-accounts/create-accounts/`, payload);
      const savedAccounts = response.data.accounts || [];

      // Merge saved accounts with existing saved accounts
      const savedIds = new Set(savedAccounts.map((acc) => acc.id));
      const existingSaved = state.bankAccounts.filter((acc) => isSaved(acc));
      const allAccounts = [...existingSaved, ...savedAccounts];

      dispatch({ type: "setBankAccounts", bankAccounts: allAccounts });
      return allAccounts;
    } catch (error) {
      console.error("Error saving bank accounts:", error);
      alert("Error saving bank accounts. Please try again.");
      throw error;
    }
  }, [state.bankAccounts, state.currency]);

  // Income CRUD methods
  const createIncome = useCallback(
    (draftData) => {
      const newIncome = {
        ...draftData,
        id: Date.now(), // Temporary ID
        amount: parseFloat(draftData.amount),
        frequency: parseInt(draftData.frequency),
        type: parseInt(draftData.type),
        bank_account: parseInt(draftData.bank_account),
      };
      dispatch({
        type: "setIncomes",
        incomes: [...state.incomes, newIncome],
      });
    },
    [state.incomes]
  );

  const updateIncome = useCallback(
    async (id, data) => {
      const income = state.incomes.find((inc) => inc.id === id);
      if (!income) {
        console.error("Income not found:", id);
        return;
      }

      const updatedIncome = {
        ...income,
        ...data,
        amount: parseFloat(data.amount),
        frequency: parseInt(data.frequency),
        type: parseInt(data.type),
        bank_account: parseInt(data.bank_account),
      };

      // If saved, update via API
      if (isSaved(income)) {
        try {
          const response = await finacraft_api.patch(`/core_api/incomes/${id}/`, {
            name: updatedIncome.name,
            amount: updatedIncome.amount,
            frequency: updatedIncome.frequency,
            type: updatedIncome.type,
            bank_account: updatedIncome.bank_account,
          });
          const savedIncome = response.data;
          dispatch({
            type: "setIncomes",
            incomes: state.incomes.map((inc) => (inc.id === id ? savedIncome : inc)),
          });
        } catch (error) {
          console.error("Error updating income:", error);
          alert("Error updating income. Please try again.");
          throw error;
        }
      } else {
        // If draft, update in state only
        dispatch({
          type: "setIncomes",
          incomes: state.incomes.map((inc) => (inc.id === id ? updatedIncome : inc)),
        });
      }
    },
    [state.incomes]
  );

  const deleteIncome = useCallback(
    async (id) => {
      const income = state.incomes.find((inc) => inc.id === id);
      if (!income) {
        console.error("Income not found:", id);
        return;
      }

      // If saved, delete via API
      if (isSaved(income)) {
        try {
          await finacraft_api.delete(`/core_api/incomes/${id}/`);
        } catch (error) {
          console.error("Error deleting income:", error);
          alert("Error deleting income. Please try again.");
          return;
        }
      }

      // Remove from state (both saved and draft)
      dispatch({
        type: "setIncomes",
        incomes: state.incomes.filter((inc) => inc.id !== id),
      });
    },
    [state.incomes]
  );

  const bulkSaveIncomes = useCallback(async () => {
    const drafts = state.incomes.filter(isDraft);
    if (drafts.length === 0) {
      return state.incomes;
    }

    try {
      const payload = {
        incomes: drafts.map((inc) => ({
          name: inc.name,
          amount: inc.amount,
          frequency: inc.frequency_detail?.id || inc.frequency,
          type: inc.type_detail?.id || inc.type,
          bank_account: inc.bank_account,
        })),
      };

      const response = await finacraft_api.post(`/core_api/incomes/create-incomes/`, payload);
      const savedIncomes = response.data.incomes || [];

      // Merge saved incomes with existing saved incomes
      const savedIds = new Set(savedIncomes.map((inc) => inc.id));
      const existingSaved = state.incomes.filter((inc) => isSaved(inc));
      const allIncomes = [...existingSaved, ...savedIncomes];

      dispatch({ type: "setIncomes", incomes: allIncomes });
      return allIncomes;
    } catch (error) {
      console.error("Error saving incomes:", error);
      alert("Error saving income sources. Please try again.");
      throw error;
    }
  }, [state.incomes]);

  // Utility methods
  const clearDrafts = useCallback(() => {
    dispatch({
      type: "setBankAccounts",
      bankAccounts: state.bankAccounts.filter(isSaved),
    });
    dispatch({
      type: "setIncomes",
      incomes: state.incomes.filter(isSaved),
    });
    dispatch({ type: "setCurrentStep", step: 0 });
    dispatch({ type: "apply", payload: { resumeStep: 1 } });
    dispatch({ type: "setSavedStep", step: 1 });
  }, [state.bankAccounts, state.incomes]);

  const setStep = useCallback((step) => {
    dispatch({ type: "setStep", step });
  }, []);

  const setCurrency = useCallback((currency) => {
    dispatch({ type: "setCurrency", currency });
  }, []);

  const getBankAccounts = useCallback(() => {
    return state.bankAccounts;
  }, [state.bankAccounts]);

  const getIncomes = useCallback(() => {
    return state.incomes;
  }, [state.incomes]);

  const getIncomesForAccount = useCallback(
    (accountId) => {
      return state.incomes.filter(
        (inc) => inc.bank_account === accountId || inc.account_id === accountId
      );
    },
    [state.incomes]
  );

  const value = {
    state,
    // CRUD methods
    createBankAccount,
    updateBankAccount,
    deleteBankAccount,
    bulkSaveBankAccounts,
    createIncome,
    updateIncome,
    deleteIncome,
    bulkSaveIncomes,
    // Utility methods
    syncFromBackend,
    clearDrafts,
    setStep,
    setCurrency,
    getBankAccounts,
    getIncomes,
    getIncomesForAccount,
    // Helpers
    isDraft,
    isSaved,
  };

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error("useOnboarding must be used inside OnboardingProvider");
  return ctx;
}
